import { useState, useEffect, useMemo, useRef } from 'react'

import { Loading } from './../../../../Common/Loading'
import { LogErrors } from './../../../../../utilities/LogErrors'
import { useLanguage } from './../../../../../utilities/LanguageContext'

import { InstrumentData } from './../../../V2/Commons/InstrumentData'


// import Prism from 'prismjs';
// // Importa el tema que quieras (ejemplo: tomorrow)
// import 'prismjs/themes/prism-tomorrow.min.css';
// // Importa los lenguajes que uses
// import 'prismjs/components/prism-python';
// // import 'prismjs/components/prism-csharp';
// // import 'prismjs/components/prism-sql';


/**
 * Mini componente con animación de plegado/desplegado usando max-height medido.
 * - Medimos scrollHeight para lograr una transición suave, incluso con imágenes.
 * - Incluye animación de opacidad.
 */
const Collapsible = ({ isOpen, children, duration = 250 }) => {
  const ref = useRef(null)
  const [maxHeight, setMaxHeight] = useState(isOpen ? 'auto' : 0)

  // Recalcular altura cuando se abre/cierra o cambia el contenido
  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Si se abre: fijar a scrollHeight y luego a 'auto' al terminar para adaptarse
    if (isOpen) {
      const h = el.scrollHeight
      setMaxHeight(h)
      const timer = setTimeout(() => setMaxHeight('auto'), duration)
      return () => clearTimeout(timer)
    } else {
      // Si estaba en auto, calculamos el alto real antes de colapsar
      if (maxHeight === 'auto') {
        const h = el.scrollHeight
        requestAnimationFrame(() => setMaxHeight(h))
        // siguiente frame, colapsar
        const timer = setTimeout(() => setMaxHeight(0), 0)
        return () => clearTimeout(timer)
      } else {
        setMaxHeight(0)
      }
    }
  }, [isOpen, children, duration]) // children por si cambia el contenido interno

  // Reaccionar a cambios de tamaño (imágenes que cargan, responsive, etc.)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      if (isOpen) {
        const h = el.scrollHeight
        if (maxHeight !== 'auto') setMaxHeight(h)
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [isOpen, maxHeight])

  return (
    <div
      style={{
        overflow: 'hidden',
        maxHeight: maxHeight === 'auto' ? 'none' : maxHeight,
        transition: `max-height ${duration}ms ease, opacity ${Math.max(150, duration - 50)}ms ease`,
        opacity: isOpen ? 1 : 0.02,
      }}
      aria-hidden={!isOpen}
    >
      <div ref={ref}>
        {children}
      </div>
    </div>
  )
}

/**
 * Botón/encabezado de sección.
 * - Para isMain:false muestra un botón toggle.
 * - Para isMain:true sólo muestra el título, sin toggle (siempre visible al inicio).
 */
const SectionHeader = ({ name, canToggle, isOpen, onToggle }) => {
  return (
    <div className="Concepts--V2__Concept__SectionHeader">
      {canToggle ? (
        <button
          type="button"
          className="Concepts--V2__Concept__SectionHeader__Button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`concept-panel-${name}`}
        >
          {/* <span className="Concepts--V2__Concept__SectionHeader__Caret">{isOpen ? '▾' : '▸'}</span> */}
          <span className="Concepts--V2__Concept__SectionHeader__Caret">{isOpen ? <i className="bi bi-chevron-down"></i> : <i className="bi bi-chevron-right"></i>}</span>
          <span className="Concepts--V2__Concept__SectionHeader__Title">{name}</span>
        </button>
      ) : (
        <div className="Concepts--V2__Concept__SectionHeader__Static">
          <span className="Concepts--V2__Concept__SectionHeader__Dot"></span>
          <span className="Concepts--V2__Concept__SectionHeader__Title">{name}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Concepts
 * @param {string}  topicName - Título del tema a mostrar
 * @param {object}  instrument - Objeto con "concept.parts"
 * @param {boolean} collapseNonMainByDefault - Si true, las secciones no principales inician plegadas
 */
const Concepts = ({ topicName, instrument, collapseNonMainByDefault = false, instrumentData = null }) => {
  const { translate } = useLanguage()

  const [isLoading, setIsLoading] = useState(true)
  const [parts, setParts] = useState([])

  // Estado de visibilidad por índice
  const [openMap, setOpenMap] = useState({}) // { [index]: boolean }

  // Inicializa parts y mapa de visibilidad
  const LoadPage = async () => {
    try {
      const list = instrument?.concept?.parts ?? []
      if (!Array.isArray(list) || list.length === 0) {
        setParts([])
        setOpenMap({})
        return
      }

      setParts(list)

      // Crear mapa de visibilidad:
      // - isMain:true => true siempre al inicio
      // - isMain:false => depende de collapseNonMainByDefault
      const initialMap = {}
      list.forEach((p, idx) => {
        initialMap[idx] = p.isMain ? true : !collapseNonMainByDefault
      })
      setOpenMap(initialMap)
    } catch (error) {
      await LogErrors('LoadPage - Concepts', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setIsLoading(true)
    LoadPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument, topicName, collapseNonMainByDefault])

  const handleToggle = (idx) => {
    setOpenMap(prev => ({ ...prev, [idx]: !prev[idx] }))
  }

  // // Dentro de tu componente Concepts
  // useEffect(() => {
  //   if (parts.length > 0) {
  //     Prism.highlightAll();
  //   }
  // }, [parts]); // Se ejecutará cuando la lista de partes se cargue o actualice

  // // const transformContent = (htmlContent) => {
  // //   // Creamos un elemento temporal para manipular el HTML
  // //   const parser = new DOMParser();
  // //   const doc = parser.parseFromString(htmlContent, 'text/html');
    
  // //   // Buscamos todos los bloques de código que envió la API
  // //   const codeBlocks = doc.querySelectorAll('pre > code');
    
  // //   codeBlocks.forEach((codeEl) => {
  // //     const parentPre = codeEl.parentElement;
  // //     const codeText = codeEl.textContent;
  // //     const language = codeEl.className.replace('language-', '');
      
  // //     // Creamos tu contenedor "cool" manualmente
  // //     const wrapper = document.createElement('div');
  // //     wrapper.className = 'mi-bloque-codigo';
  // //     wrapper.innerHTML = `
  // //       <div class="mi-bloque-cabecera">
  // //         <div class="mi-bloque-titulo">
  // //         <span class="mi-bloque-archivo">codigo.${language === 'python' ? 'py' : language}</span>
  // //         </div>
  // //         <span class="mi-bloque-lenguaje">${language.toUpperCase()}</span>
  // //       </div>
  // //       <pre><code class="language-${language}">${codeText}</code></pre>
  // //     `;
      
  // //     // Reemplazamos el <pre> viejo por tu nuevo contenedor
  // //     parentPre.replaceWith(wrapper);
  // //   });
    
  // //   return doc.body.innerHTML;
  // // };

  // const transformContent = (htmlContent) => {
  //   const parser = new DOMParser();
  //   const doc = parser.parseFromString(htmlContent, 'text/html');
  //   const codeBlocks = doc.querySelectorAll('pre > code');

  //   // Configuración de lenguajes y sus extensiones/nombres para el encabezado
  //   const languageConfig = {
  //     'python': { ext: 'py', name: 'PYTHON' },
  //     'javascript': { ext: 'js', name: 'JAVASCRIPT' },
  //     'sql': { ext: 'sql', name: 'SQL' },
  //     'csharp': { ext: 'cs', name: 'C#' }
  //   };

  //   codeBlocks.forEach((codeEl) => {
  //     const parentPre = codeEl.parentElement;
  //     const codeText = codeEl.textContent;
      
  //     // Extraemos la clase (ej: "language-python" -> "python")
  //     const langClass = Array.from(codeEl.classList).find(c => c.startsWith('language-'));
  //     const langKey = langClass ? langClass.replace('language-', '') : 'text';
  //     const config = languageConfig[langKey] || { ext: 'txt', name: langKey.toUpperCase() };

  //     // Creamos tu contenedor
  //     const wrapper = document.createElement('div');
  //     wrapper.className = 'mi-bloque-codigo';
      
  //     // Inyectamos el diseño con el logo dinámico
  //     wrapper.innerHTML = `
  //       <div class="mi-bloque-cabecera">
  //         <div class="mi-bloque-titulo">
  //           ${getLogoSvg(langKey)}
  //           <span class="mi-bloque-archivo">codigo.${config.ext}</span>
  //         </div>
  //         <span class="mi-bloque-lenguaje">${config.name}</span>
  //       </div>
  //       <pre><code class="${langClass}">${codeText}</code></pre>
  //     `;
      
  //     parentPre.replaceWith(wrapper);
  //   });
    
  //   return doc.body.innerHTML;
  // };

  // const getLogoSvg = (lang) => {
  //   if (lang === 'python') {
  //     return `<svg class="w-4 h-4" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 16px; height: 16px;">
  //               <path d="M54 2C32.1 2 27.5 11.2 27.5 25.1v10.9h27V39h-38C7.5 39 2 45.4 2 61.1c0 14.1 6.3 22.1 19 22.1h10.9v-15c0-15.3 12.4-27.7 27.7-27.7h20.1V15c0-8.8-7-13-15-13H54zm-13 8.3c3.4 0 6.2 2.8 6.2 6.2s-2.8 6.2-6.2 6.2-6.2-2.8-6.2-6.2 2.8-6.2 6.2-6.2z" fill="#38bdf8"/>
  //               <path d="M56 108c21.9 0 26.5-9.2 26.5-23.1V74h-27V70.1h38c9 0 14.5-6.4 14.5-22.1 0-14.1-6.3-22.1-19-22.1H78.1v15C78.1 56.3 65.7 68.7 50.4 68.7H30.3V95c0 8.8 7 13 15 13H56zm13-8.3c-3.4 0-6.2-2.8-6.2-6.2s2.8-6.2 6.2-6.2 6.2 2.8 6.2 6.2-2.8 6.2-6.2 6.2z" fill="#fbbf24"/>
  //             </svg>`;
  //   }
  //   // Puedes agregar más casos aquí o poner un ícono genérico
  //   return `<i class="bi bi-code-slash"></i>`; 
  // };

  // Sanitizador opcional:
  const safeHtml = (html) => {
    // return { __html: DOMPurify.sanitize(html || '') }
    return { __html: html || '' }
  }

  const hasContent = parts.length > 0

  return isLoading ? (
    <Loading />
  ) : (
    <div className='container Concepts--V2 Fonts--PlusJakartaSans'>
      {hasContent ? (
        <>
        {/* // <div className='Tables--Shadow'>
        //     <p className='Concepts--V2__Text--Title'>{topicName}</p>
        // </div> */}
        {/* <p className='Concepts--V2__Text--Title'>{topicName}</p> */}

        <InstrumentData data={instrumentData} />

        <div className='Concepts--V2__Concept'>

          <div className="Concepts__List" role="list">
            {parts.map((part, idx) => {
              const isMain = !!part.isMain
              const isOpen = !!openMap[idx]
              const canToggle = !isMain // Sólo las no principales son plegables

              return (
                <section key={`${part.name}-${idx}`} className="Concepts--V2__Concept__Section" role="listitem">
                  <SectionHeader
                    name={part.name}
                    canToggle={canToggle}
                    isOpen={isOpen}
                    onToggle={() => canToggle && handleToggle(idx)}
                  />

                  {/* Panel de contenido con animación */}
                  <Collapsible isOpen={isOpen}>
                    <div
                      id={`concept-panel-${part.name}`}
                      className="Concepts--V2__Concept__Section__Content"
                      // Si sanitizas, usa safeHtml(part.content)
                      dangerouslySetInnerHTML={safeHtml(part.content)}
                    />
                  </Collapsible>

                  {/* <Collapsible isOpen={isOpen}>
                    {/* // En tu mapeo de partes, procesa el contenido antes de insertarlo /}
                    <div
                      id={`concept-panel-${part.name}`}
                      className="Concepts--V2__Concept__Section__Content"
                      dangerouslySetInnerHTML={{ __html: transformContent(part.content) }}
                    />
                  </Collapsible> */}
                </section>
              )
            })}
          </div>
        </div>
        </>
      ) : (
        <p>{translate('labels.viewer.teaching.concepts.empty')}</p>
      )}
    </div>
  )
}

export { Concepts };